import {
	GenerateCopywritingResponse,
	ToneOfVoiceMessageType,
} from "@/pages/Private/pages/Messages/components/GenerateBar/types";

import { Draft } from "./inboxDraft.types";

type StepKey = "step_1" | "step_2" | "step_3";

type SequenceField =
	| { step: StepKey; kind: "linkedin_connection_request_note" }
	| { step: StepKey; kind: "linkedin_message" }
	| { step: StepKey; kind: "email" };

/**
 * Inverse of the forward map in `sequenceToDraftItems.util.ts`: tells us which
 * `sequenceOutput.sequence.step_X` field a draft's edited content belongs to,
 * based on the tone-of-voice message type carried on `draft.aiOrigin`.
 */
const MESSAGE_TYPE_TO_FIELD: Partial<Record<ToneOfVoiceMessageType, SequenceField>> = {
	[ToneOfVoiceMessageType.LI_CONNECTION_REQUEST]: {
		step: "step_1",
		kind: "linkedin_connection_request_note",
	},
	[ToneOfVoiceMessageType.LI_INTRODUCTION]: { step: "step_1", kind: "linkedin_message" },
	[ToneOfVoiceMessageType.LI_FOLLOW_UP]: { step: "step_2", kind: "linkedin_message" },
	[ToneOfVoiceMessageType.LI_BREAKUP]: { step: "step_3", kind: "linkedin_message" },
	[ToneOfVoiceMessageType.EMAIL_INTRODUCTION]: { step: "step_1", kind: "email" },
	[ToneOfVoiceMessageType.EMAIL_FOLLOW_UP]: { step: "step_2", kind: "email" },
	[ToneOfVoiceMessageType.EMAIL_BREAKUP]: { step: "step_3", kind: "email" },
};

/**
 * Overlays the user's draft edits onto a deep copy of the original AI
 * `copywritingOutput`, returning the full updated object ready to be PATCHed to
 * `list_person/:id/copywriting-output`.
 *
 * Only `full_sequence` output is mutated; `contextPayload`, `messagingStrategy`,
 * `validation`, `generation_notes` and any untouched step/channel are preserved
 * exactly (the field is replaced wholesale by the backend, so we must keep them).
 *
 * Drafts without an `aiOrigin` (e.g. manually-added follow-ups) are not part of
 * the AI sequence and are ignored. Because only the active channel's drafts are
 * loaded into the editor, the other channel's messages keep their original text.
 */
export const applyDraftEditsToCopywritingOutput = (
	original: GenerateCopywritingResponse | null | undefined,
	drafts: Draft[]
): GenerateCopywritingResponse | null | undefined => {
	if (!original || original.type !== "full_sequence") {
		return original;
	}

	const next = structuredClone(original);
	const sequence = next.sequenceOutput?.sequence;

	if (!sequence) {
		return next;
	}

	for (const draft of drafts) {
		const { aiOrigin } = draft;

		if (!aiOrigin) {
			continue;
		}

		const target = MESSAGE_TYPE_TO_FIELD[aiOrigin.messageType];

		if (!target) {
			continue;
		}

		const step = sequence[target.step];

		if (!step) {
			continue;
		}

		if (target.kind === "email") {
			if (!step.email) {
				continue;
			}

			step.email.body = draft.content;

			if (draft.subject !== undefined) {
				step.email.subject_line = draft.subject;
			}
		} else {
			step[target.kind] = draft.content;
		}
	}

	return next;
};

/**
 * Returns `true` when at least one AI draft's text (or email subject) differs from
 * the value currently stored on `copywritingOutput`. Used to only surface the
 * "Save draft" action once the user has actually altered a message. Drafts without
 * an `aiOrigin` (manually-added follow-ups) are not part of the AI sequence and are
 * ignored. Comparing against the stored output — which is updated on save — means a
 * successful save (or reverting an edit) clears the "dirty" state.
 */
export const copywritingDraftsHaveEdits = (
	original: GenerateCopywritingResponse | null | undefined,
	drafts: Draft[]
): boolean => {
	if (!original || original.type !== "full_sequence") {
		return false;
	}

	const sequence = original.sequenceOutput?.sequence;

	if (!sequence) {
		return false;
	}

	return drafts.some((draft) => {
		const { aiOrigin } = draft;

		if (!aiOrigin) {
			return false;
		}

		const target = MESSAGE_TYPE_TO_FIELD[aiOrigin.messageType];

		if (!target) {
			return false;
		}

		const step = sequence[target.step];

		if (!step) {
			return false;
		}

		if (target.kind === "email") {
			if (!step.email) {
				return false;
			}

			if (draft.content !== step.email.body) {
				return true;
			}

			return draft.subject !== undefined && draft.subject !== step.email.subject_line;
		}

		return draft.content !== step[target.kind];
	});
};
